import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { ApplicationFormPageEnum } from '../types';
import { AddAddress } from '../AddAddress';
import { IAddressDetails } from '../AddAddress/types';
import { AddressHistory } from '../AddressHistory';
import { AddressWrapperType } from './types';
import { applicationFormApi } from 'api/applicationForm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { getCurrentRoute } from 'utils/location';
import { IApplicationInProgressQuestion } from '../types';
import { parseApplicationInProgress } from '../helpers';
import { addAddressQuestions } from '../AddAddress/questions';

export const AddressWrapper = () => {
  const intl = useIntl();
  const { id = '' } = useParams();
  const { data: applicationInProgress, refetch, isLoading } = useApplicationInProgressById(id);
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress, {
    onSuccess: refetch,
  });
  const currentRoute = getCurrentRoute();
  const [addressHistory, setAddressHistory] = useState<IAddressDetails[]>([]);
  const [currentPage, setCurrentPage] = useState<AddressWrapperType>(ApplicationFormPageEnum.ADDRESS_HISTORY);
  const [addressToEdit, setAddressToEdit] = useState<IAddressDetails | null>(null);
  const isEdit = !!addressToEdit;

  const addressFormatter = (address: IAddressDetails): IApplicationInProgressQuestion[] => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...addressDetails } = address;
    const isPartOfDate = ['month', 'year'];

    const filteredAddressDetails = Object.entries(addressDetails).filter(([, value]) => value);

    return filteredAddressDetails.map(([key, value]: any) => {
      const question = isPartOfDate.includes(key)
        ? `${intl.formatMessage({
            id: addAddressQuestions['whenMoveDate'].labelKey,
          })} (${key})`
        : intl.formatMessage({
            id: addAddressQuestions[key].labelKey,
          });

      return {
        question,
        key,
        answer: value,
      };
    });
  };

  useEffect(() => {
    if (applicationInProgress?.[currentRoute]) {
      const formattedResponse = applicationInProgress[currentRoute].map((card: any) => ({
        id: uuidv4(),
        ...parseApplicationInProgress(card),
      })) as IAddressDetails[];
      setAddressHistory(formattedResponse);
    }
  }, [applicationInProgress]);

  const patchAddressHistory = (addressesList: IAddressDetails[]) => {
    const questions = addressesList.map(addressFormatter);

    patchApplicationInProgress({ questions, currentRoute, applicationId: id });
  };

  const editAddressRedirectHandler = (address: IAddressDetails) => {
    setAddressToEdit(address);
    setCurrentPage(ApplicationFormPageEnum.ADD_ADDRESS);
  };

  const addAddressHandler = (newAaddress: IAddressDetails) => {
    patchAddressHistory([...addressHistory, newAaddress]);
    setCurrentPage(ApplicationFormPageEnum.ADDRESS_HISTORY);
  };

  const goBackHandler = () => {
    setCurrentPage(ApplicationFormPageEnum.ADDRESS_HISTORY);
    setAddressToEdit(null);
  };

  const editAddressHandler = (editedAddress: IAddressDetails) => {
    const newAddressHistory = addressHistory.map(addressUnit => {
      return addressUnit.id === editedAddress.id ? editedAddress : addressUnit;
    });

    patchAddressHistory(newAddressHistory);
    goBackHandler();
  };

  const removeAddressByIdHandler = (id: string) =>
    setAddressHistory(addressHistory.filter(address => address.id !== id));

  const submitAddressHandler = (address: IAddressDetails) =>
    isEdit ? editAddressHandler(address) : addAddressHandler(address);

  const ActiveFormPage = () => {
    switch (currentPage) {
      case ApplicationFormPageEnum.ADDRESS_HISTORY:
        return (
          <AddressHistory
            addressHistory={addressHistory}
            onAddAddressRedirect={() => setCurrentPage(ApplicationFormPageEnum.ADD_ADDRESS)}
            onEditAddressRedirect={editAddressRedirectHandler}
            onRemoveAddressById={removeAddressByIdHandler}
          />
        );
      case ApplicationFormPageEnum.ADD_ADDRESS:
        return (
          <AddAddress addressToEdit={addressToEdit} onSubmitAddress={submitAddressHandler} onGoBack={goBackHandler} />
        );
      default:
        return null;
    }
  };

  return isLoading ? null : <ActiveFormPage />;
};
